import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function FarmerHubConnections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingHub, setEditingHub] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shareAll, setShareAll] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [connRes, prodRes] = await Promise.all([
        fetch('/api/farm/hub-connections', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/products/my-products', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      if (!connRes.ok) throw new Error('Failed to fetch connections');
      if (!prodRes.ok) throw new Error('Failed to fetch products');
      const connData = await connRes.json();
      const prodData = await prodRes.json();
      setConnections(Array.isArray(connData) ? connData : []);
      setAllProducts(Array.isArray(prodData) ? prodData : []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (connection) => {
    setEditingHub(connection);
    const currentProductIds = connection.products?.map(p => p.productId) || [];
    setSelectedProducts(currentProductIds);
    setShareAll(connection.shareAllProducts || false);
  };

  const saveProducts = async () => {
    if (!editingHub) return;
    try {
      const res = await fetch(`/api/farm/hub-connections/${editingHub.hub._id}/products`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ productIds: selectedProducts, shareAll })
      });
      if (!res.ok) throw new Error(await res.text());
      alert('Products updated successfully');
      setEditingHub(null);
      fetchData();
    } catch (error) {
      console.error(error);
      alert('Failed to update products');
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  if (loading) return <div className="text-center py-8">Loading hub connections...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  if (connections.length === 0) return <div className="text-center py-8">You are not connected to any hubs yet.</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">My Hub Connections</h1>
      <div className="space-y-6">
        {connections.map(conn => (
          <div key={conn._id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold">{conn.hub?.name || 'Unknown Hub'}</h2>
                <p className="text-gray-600 text-sm mt-1">{conn.hub?.email}</p>
                <p className="text-sm text-gray-500 mt-2">Default commission: {conn.hub?.defaultCommission || 10}%</p>
                <p className="text-sm">Shared products: {conn.shareAllProducts ? 'All products (current and future)' : `${conn.products?.length || 0} product(s)`}</p>
              </div>
              <button onClick={() => openEditModal(conn)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Manage Products</button>
            </div>
          </div>
        ))}
      </div>

      {editingHub && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Manage products for {editingHub.hub?.name}</h2>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={shareAll} onChange={() => setShareAll(!shareAll)} />
                <span>Share all my products (including future ones)</span>
              </label>
            </div>
            {!shareAll && (
              <div className="space-y-2 max-h-96 overflow-y-auto border rounded p-3">
                <p className="font-medium mb-2">Select products to share:</p>
                {allProducts.map(product => {
                  const isShared = selectedProducts.includes(product._id);
                  return (
                    <label key={product._id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input type="checkbox" checked={isShared} onChange={() => toggleProduct(product._id)} className="w-4 h-4" />
                      <div className="flex-1">
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-gray-500">${product.price}/{product.unit}</div>
                      </div>
                      {isShared && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Shared</span>}
                    </label>
                  );
                })}
                {allProducts.length === 0 && <p className="text-gray-500 text-center py-4">You haven't added any products yet.</p>}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingHub(null)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
              <button onClick={saveProducts} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
